import React from 'react';
import './3D renderingandvisualations.css';
import QuoteForm from '../../components/QuoteForm';
import SEO from '../../components/SEO';

const RenderingVisualizationPage = () => {

  const services = [
    {
      title: 'Interior 3D Rendering',
      description: 'Photorealistic interior designs showcasing every detail from furniture to lighting.',
      icon: '🏠'
    },
    {
      title: 'Exterior Visualization',
      description: 'Stunning exterior renders that bring architectural concepts to life.',
      icon: '🏢'
    },
    {
      title: '3D Floor Plans',
      description: 'Detailed 3D floor plans that help visualize spatial relationships and flow.',
      icon: '📐'
    },
    {
      title: '360° Walkthroughs',
      description: 'Immersive virtual tours allowing you to explore spaces before construction.',
      icon: '🔄'
    },
    {
      title: 'Product Rendering',
      description: 'High-quality 3D product visualizations for marketing and design approval.',
      icon: '🎨'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Consultation',
      description: 'Discuss your vision, requirements, and project scope with our experts.'
    },
    {
      step: '2',
      title: 'Concept Development',
      description: 'We create initial concepts and mood boards based on your preferences.'
    },
    {
      step: '3',
      title: '3D Modeling',
      description: 'Detailed 3D models are built with precise measurements and specifications.'
    },
    {
      step: '4',
      title: 'Rendering & Revisions',
      description: 'Photorealistic rendering with unlimited revisions until you\'re satisfied.'
    },
    {
      step: '5',
      title: 'Final Delivery',
      description: 'High-resolution images and files delivered in your preferred format.'
    }
  ];

  const benefits = [
    {
      title: 'Photorealistic Results',
      description: 'Experience your space before it\'s built with stunning realism.',
      icon: '✨'
    },
    {
      title: 'Saves Time & Cost',
      description: 'Avoid costly mistakes by visualizing designs before construction.',
      icon: '💰'
    },
    {
      title: 'Better Decision Making',
      description: 'Make informed choices about materials, colors, and layouts.',
      icon: '🎯'
    },
    {
      title: 'Custom Designs',
      description: 'Tailored visualizations that match your unique style and needs.',
      icon: '🎨'
    },
    {
      title: 'Fast Turnaround',
      description: 'Quick delivery without compromising on quality.',
      icon: '⚡'
    }
  ];

  const portfolioItems = [
    { title: 'Modern Living Room', category: 'Residential', image: '🛋️' },
    { title: 'Executive Office', category: 'Commercial', image: '💼' },
    { title: 'Retail Space', category: 'Commercial', image: '🏪' },
    { title: 'Luxury Kitchen', category: 'Residential', image: '🍳' },
    { title: 'Restaurant Interior', category: 'Hospitality', image: '🍽️' },
    { title: 'Master Bedroom', category: 'Residential', image: '🛏️' }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      location: 'Nairobi',
      text: 'The 3D rendering helped us visualize our dream home perfectly. We could make changes before construction started, saving us thousands!',
      rating: 5
    },
    {
      name: 'James K.',
      location: 'Mombasa',
      text: 'Professional service with incredible attention to detail. The walkthrough was amazing and helped us secure investor approval.',
      rating: 5
    },
    {
      name: 'Linda W.',
      location: 'Kisumu',
      text: 'Fast turnaround and exceptional quality. The team understood our vision and delivered beyond expectations.',
      rating: 5
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "3D Rendering & Visualization",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "telephone": "+254748600208",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": "Kenya",
    "description": "Professional 3D rendering and visualization services in Nairobi Kenya. Interior, exterior, 3D floor plans, and 360° walkthroughs for architectural projects."
  };

  return (
    <div className="rendering-page">
      {/* SEO Component */}
      <SEO
        title="3D Rendering & Architectural Visualization Services Kenya | Techie Interiors"
        description="Professional 3D rendering and visualization services in Nairobi Kenya. Photorealistic interior and exterior renders, 3D floor plans, and 360° virtual walkthroughs."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/3d-rendering-visualization"
        structuredData={structuredData}
      />
      {/* 1. HERO SECTION */}
      <section className="rendering-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>3D Rendering & Visualization Services in Kenya</h1>
          <p>Experience your space before it's built with photorealistic 3D designs.</p>
          <div className="hero-buttons">
            <a href="#contact-form" className="btn btn-primary">Request a Quote</a>
            <a href="#portfolio" className="btn btn-secondary">View Portfolio</a>
          </div>
        </div>
      </section>

      {/* 2. TRUST + INTRO SECTION */}
      <section className="rendering-intro">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-text">
              <h2 className="section-title-left">Transform Your Vision Into Reality</h2>
              <p>
                At <strong>Techie Interiors</strong>, we specialize in creating stunning <strong>3D rendering Kenya</strong> solutions that bring your architectural and interior design concepts to life. Our expertise in <strong>interior visualization</strong> and <strong>architectural visualization</strong> helps homeowners, businesses, and developers across Kenya make confident design decisions.
              </p>
              <p>
                With cutting-edge technology and years of experience, we deliver <strong>realistic 3D designs</strong> that capture every detail, from lighting and textures to furniture placement and spatial flow. Whether you're planning a new build, renovation, or interior redesign, our visualization services ensure you see the final result before construction begins.
              </p>
            </div>
            <div className="intro-image">
              <div className="image">
                <img src="/techLogo.jpeg" alt="3D rendering and architectural visualization services by Techie Interiors" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SERVICES BREAKDOWN */}
      <section className="rendering-services">
        <div className="container">
          <h2 className="section-title">Our 3D Visualization Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. VISUAL PORTFOLIO SECTION */}
      <section className="rendering-portfolio" id="portfolio">
        <div className="container">
          <h2 className="section-title">Our Portfolio</h2>
          <div className="portfolio-grid">
            {portfolioItems.map((item, index) => (
              <div key={index} className="portfolio-card">
                <div className="portfolio-image">{item.image}</div>
                <div className="portfolio-overlay">
                  <h3>{item.title}</h3>
                  <p>{item.category}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PROCESS SECTION */}
      <section className="rendering-process">
        <div className="container">
          <h2 className="section-title">Our Process</h2>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. BENEFITS SECTION */}
      <section className="rendering-benefits">
        <div className="container">
          <h2 className="section-title">Why Choose Our 3D Rendering Services?</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SEO CONTENT BLOCK */}
      <section className="rendering-seo">
        <div className="container">
          <div className="seo-content">
            <h2 className="section-title-left">Professional 3D Rendering Services in Kenya</h2>
            <p>
              Techie Interiors is a leading provider of <strong>3D rendering services in Kenya</strong>, offering comprehensive <strong>interior design visualization</strong> solutions for residential, commercial, and industrial projects. Our team of skilled 3D artists and designers creates <strong>architectural rendering</strong> that captures the essence of your design vision with stunning accuracy and detail.
            </p>
            <p>
              Our <strong>realistic 3D designs</strong> serve as powerful tools for architects, interior designers, real estate developers, and homeowners. By visualizing spaces before construction begins, you can make informed decisions about layouts, materials, color schemes, and furniture placement—ultimately saving time, reducing costs, and ensuring the final result matches your expectations.
            </p>
            <p>
              Whether you need <strong>interior visualization</strong> for a single room, a complete home, or a large commercial space, our team delivers exceptional quality with fast turnaround times. We use industry-leading software and techniques to create photorealistic images, 360° virtual tours, and animated walkthroughs that bring your designs to life.
            </p>
          </div>
        </div>
      </section>

     {/* 9. CONTACT FORM */}
      <section className="rendering-form-section" id="contact-form">
        <div className="container">
          <h2 className="section-title">Request a 3D Rendering Quote</h2>
          <div className="form-wrapper">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>Have questions about our 3D rendering services? We're here to help bring your vision to life.</p>
              <div className="info-item">
                <strong>Phone:</strong>
                <span>+254748600208</span>
              </div>
              <div className="info-item">
                <strong>Email:</strong>
                <span>info@techieinteriors.co.ke</span>
              </div>
              <div className="info-item">
                <strong>Location:</strong>
                <span>Nairobi, Kenya</span>
              </div>
              <div className="trust-line">
                <strong>We respond instantly.</strong>
              </div>
            </div>
            <QuoteForm serviceName="3D Rendering & Visualization" />
          </div>
        </div>
      </section>

      {/* 10. TESTIMONIALS / TRUST ELEMENTS */}
      <section className="rendering-trust">
        <div className="container">
          <h2 className="section-title">Trusted Across Kenya</h2>
          <p className="trust-subtitle">We've worked with homeowners, businesses, and developers across Kenya</p>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="rating">{'⭐'.repeat(testimonial.rating)}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RenderingVisualizationPage;
